import { useState } from "react";
import { useTranslation } from "react-i18next";
import {Dialog, DialogTitle, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, Tooltip, styled, tooltipClasses  } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { yellow } from "@mui/material/colors";
import InputIcon from "@mui/icons-material/Input";
import OutputIcon from "@mui/icons-material/Output";
import CategoryIcon from "@mui/icons-material/Category";
import { useDispatch, useSelector } from "react-redux";
import { treeActions } from "../../redux/slices/treeSlice";
import LightTooltip from "../../commonComponents/LightTooltip";

export default function SaveWarning({warningsModalOpen, setWarningsModalOpen, unsavedList, selectedTemplate, handleModalClose}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentScreen } = useSelector((state) => state.treeData);
  const currFilter = unsavedList.filter(o => selectedTemplate === o.path);
  const [currentTemplate, setCurrentTemplate] = useState(
    currFilter.length > 0 ? currFilter[0] : unsavedList[0]
  );
  const rotateDisabled = unsavedList.length === 1;

  const closeModal = () => setWarningsModalOpen(false);

  //on View Template click, change tabs(if needed) and dispatch call to force select template.
  const handleViewTemplate = () => {
    if (currentScreen !== currentTemplate.template_type) {
      dispatch(treeActions.setCurrentScreen(currentTemplate.template_type));
    }
    
    dispatch(treeActions.setForceTemplate({
      workflowGroup: currentTemplate.workflow_group,
      template: currentTemplate.template,
      screen: currentTemplate.template_type
    }));
    
    closeModal();
    handleModalClose();
  }

  //change the current viewed template and warnings
  const rotateTemplate = (inc) => {
    const getCurrIndex = () => {
      let curr = 0;
      unsavedList.forEach((o, i) => {
        if (o.path === currentTemplate.path) curr = i;
      })
      return curr;
    }

    if (inc) {
      //if incrementing forwards, set curr template +1 or 0 if already at max 
      setCurrentTemplate(
        (getCurrIndex() + 1) <= (unsavedList.length - 1) ? 
        unsavedList[getCurrIndex() + 1] : unsavedList[0]
      )
    } else {
      //if incrementing backwards, set curr template -1 or max if already at 0
      setCurrentTemplate(
        (getCurrIndex() - 1) >= 0 ? 
        unsavedList[getCurrIndex() - 1] : unsavedList[unsavedList.length > 0 ? unsavedList.length - 1 : 0]
      )
    }
  }

  const WarningList = () => {
    return currentTemplate.warnings.map((o, i) => (
      <TableRow
        hover
        key={`${i}-warning`}
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      >
        <TableCell>
          {i + 1}
        </TableCell>
        <TableCell>
          {`${o.split(':')[2]} ${o.split(':')[0]}`}
        </TableCell>
      </TableRow>
    ));
  };

  const TemplateInfo = () => (
    <div style={styles.templateContainer} className="flex">
      <div className="flex align-center divider-right padding-8">
        <LightTooltip 
          placement="top"
          title={currentTemplate.template_type.charAt(0).toUpperCase() + currentTemplate.template_type.slice(1)} 
        >
          {typeIcons[currentTemplate.template_type]}
        </LightTooltip>
      </div>
      <div className="margin-left-12">
        <div className="flex padding-bottom-4 padding-top-8">
          <div style={styles.label}>{t("ACCOUNT")}:</div>
          <div>{currentTemplate.account}</div>
        </div>
        <div className="flex padding-bottom-4">
          <div style={styles.label}>{t("LBL_WF_GROUP")}</div>
          <div>{currentTemplate.workflow_group}</div>
        </div>
        <div className="flex padding-bottom-8">
          <div style={styles.label}>{t("TEMPLATE")}:</div>
          <div>{currentTemplate.template}</div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Dialog 
        maxWidth="md" 
        fullWidth
        open={warningsModalOpen} 
        onClose={closeModal}
        className="dialog-top"
      >
        <DialogTitle sx={{padding: "16px 16px 16px 24px"}} className="divider-bottom">
          <div className="flex space-between">
            <div className="dialog-header">
              <div className="title flex">
                <WarningRoundedIcon sx={styles.warningIcon} />
                {t('DIAG_WARNINGS_TIT')}
              </div>
              <div className="description">{t('DIAG_WARNINGS_DESC')}</div>
            </div>
            <div 
              onClick={closeModal} 
              className="dialog-close"
            >
              <CloseIcon sx={{fill: '#666666', fontSize: "28px"}} />
            </div>
          </div>
        </DialogTitle>
        <div className="padding-12 font-12" style={{maxHeight: "70vh", overflow:'hidden'}}>
          {/* content top */}
          <div style={styles.topContent}> 
            <Button sx={styles.verticalButton} onClick={() => rotateTemplate(false)} disabled={rotateDisabled}>
              <KeyboardDoubleArrowLeftIcon />
            </Button>
            <TemplateInfo />
            <Button sx={styles.verticalButton} onClick={() => rotateTemplate(true)} disabled={rotateDisabled}>
              <KeyboardDoubleArrowRightIcon />
            </Button>
          </div>

          {/* Warning list table */}
          <TableContainer sx={{boxShadow: 0, maxHeight: "350px", overflowX: 'hidden', overflowY: 'auto'}} className="table-border">
            <Table stickyHeader size="small" sx={{ minWidth: 550 }}>
              <TableHead>
                <TableRow>
                  <TableCell width={"20px"} align="left">#</TableCell>
                  <TableCell align="left">{t('COL_WARNING_MESSAGE')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <WarningList />
              </TableBody>
            </Table>
          </TableContainer> 
        </div>

        {/* Dialog Actions */}
        <div className="padding-12 divider-top flex align-center justify-end">
          <Button 
            size='small'
            onClick={handleViewTemplate} 
            variant="outlined" 
            className="margin-right-8"
          >
            {t('BTN_VIEW_SOURCE')}
          </Button>
          <Button 
            size='small'
            onClick={() => setWarningsModalOpen(false)} 
            variant="outlined"
          >
            {t('BTN_RETURN_TO_SAVE_DIALOG')}
          </Button>
        </div>
      </Dialog>
    </>
  )
}

const styles = {
  verticalButton: {
    border: "1px solid #e0e0e0",
    padding: "4px !important",
    minWidth: "24px",
    height: "100% !important",
    minHeight: '68px !important',
    fill: "#666666"
  },
  topContent: {
    marginBottom: "12px",
    display: "flex"
  },
  templateContainer: {
    border: "1px solid #e0e0e0",
    borderRadius: "5px",
    // padding: "4px",
    flexGrow: "1",
    margin: "0 8px"
  },
  warningIcon: {
    fontSize: "24px", 
    marginRight: "2px",
    color: yellow[700], 
    // color: "#666666",
  },
  dialog: {
    alignItems: 'flex-start !important'
  },
  label: {
    fontWeight: 'bold',
    width: '110px'
  },
  icon: {fill: '#666666', fontSize: "19px"},
  noWarnings: {
    padding: '16px',
    width: '100%'
  }
}

const typeIcons = {
  inbound: <InputIcon sx={styles.icon}/>,
  outbound: <OutputIcon sx={styles.icon}/>,
  workflow: <CategoryIcon sx={styles.icon}/>,
}

const TemplateTypeTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }}/>
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: 12,
    fontWeight: 500,
  },
}));